<script lang="ts" setup>
import VueSelect from 'vue-select'
import { createPopper } from '@popperjs/core'
import { defaultLocale, type LocaleCode } from '~/i18n/config'

const { languageCode } = useLocale()
const { locales, setLocale } = useI18n()
const { setLoading } = useLoadingStore()
const { updateAllContexts, getLanguageByCode } = useShopLanguages()
const { apiClient } = useShopwareContext()
const { changeLanguage } = useInternationalization()
const { refreshSessionContext } = useSessionContext()
const switchLocalePath = useSwitchLocalePath()
const store = useLanguageSwitcherStore()
const { setPreviewLanguage, clearPreviewLanguage } = useMenuStore()

const links = computed(() => store.drupal.links)
const entityId = computed(() => store.shopware.entityId)
const isDrupal = computed(() => Boolean(store.drupal.links.length))
const isShopware = computed(() => Boolean(store.shopware.entityId))

interface Option {
  label: string
  value: LocaleCode
}

interface LocaleItem {
  name: string
  code: LocaleCode
}

const options = (locales.value as LocaleItem[]).map(
  ({ name, code }): Option => ({
    label: name,
    value: code,
  }),
)

const current = computed(() => {
  const found = options.find((option) => option.value === languageCode.value)
  return found || options[0]
})

const onChange = async ({ value: code }: Option) => {
  setLoading(true)

  try {
    await setPreviewLanguage(code)

    if (isShopware.value) {
      try {
        const language = await getLanguageByCode(code)
        apiClient.defaultHeaders.apply({ 'sw-language-id': language.id })
        await changeLanguage(language.id)
        await refreshSessionContext()
        await new Promise((resolve) => setTimeout(resolve, 100))
      } catch (error) {
        console.error('Failed to update Shopware context:', error)
      }
    }

    if (isDrupal.value) {
      const targetLink = links.value.find((link) => link.locale === code)
      if (targetLink) {
        await navigateTo(targetLink.path)
      } else {
        console.warn('No target link found for Drupal content')
        return
      }
    } else if (isShopware.value && entityId.value) {
      try {
        const language = await getLanguageByCode(code)

        const allSeoUrlsResponse = await apiClient.invoke('readSeoUrl post /seo-url', {
          body: {
            filter: [
              {
                type: 'equals',
                field: 'foreignKey',
                value: entityId.value,
              },
            ],
            associations: {
              language: {},
            },
          },
        })

        const response = await apiClient.invoke('readSeoUrl post /seo-url', {
          body: {
            filter: [
              {
                type: 'equals',
                field: 'foreignKey',
                value: entityId.value,
              },
              {
                type: 'equals',
                field: 'languageId',
                value: language.id,
              },
            ],
            associations: {
              language: {},
            },
          },
        })

        if (response.data?.elements?.length > 0) {
          const element = response.data.elements[0]
          if (element.seoPathInfo) {
            const path = code === defaultLocale ? `/${element.seoPathInfo}` : `/${code}/${element.seoPathInfo}`
            await navigateTo(path)
          } else {
            console.warn(`No SEO path found for entity ${entityId.value}`)
            return
          }
        } else {
          const anySeoUrl = allSeoUrlsResponse.data?.elements?.[0]
          if (anySeoUrl) {
            const isCategory = anySeoUrl.routeName === 'frontend.navigation.page'
            const isProduct = anySeoUrl.routeName === 'frontend.detail.page'

            try {
              if (isCategory) {
                await apiClient.invoke('readCategoryList post /category', {
                  body: {
                    filter: [{ type: 'equals', field: 'id', value: entityId.value }],
                    associations: { seoUrls: {} },
                  },
                })
              } else if (isProduct) {
                await apiClient.invoke('readProductListing post /product-listing/{categoryId}', {
                  pathParams: { categoryId: '' },
                  body: {
                    filter: [{ type: 'equals', field: 'id', value: entityId.value }],
                    associations: { seoUrls: {} },
                  },
                })
              }

              const retryResponse = await apiClient.invoke('readSeoUrl post /seo-url', {
                body: {
                  filter: [
                    { type: 'equals', field: 'foreignKey', value: entityId.value },
                    { type: 'equals', field: 'languageId', value: language.id },
                  ],
                },
              })

              if (retryResponse.data?.elements?.length > 0) {
                const element = retryResponse.data.elements[0]
                if (element.seoPathInfo) {
                  const path = code === defaultLocale ? `/${element.seoPathInfo}` : `/${code}/${element.seoPathInfo}`
                  await navigateTo(path)
                  return
                }
              }
            } catch (error) {
              console.warn('Failed to generate SEO URL:', error)
            }

            if (isCategory) {
              const technicalPath =
                code === defaultLocale
                  ? `/shop/navigation/${entityId.value}`
                  : `/${code}/shop/navigation/${entityId.value}`
              await navigateTo(technicalPath)
            } else if (isProduct) {
              const technicalPath =
                code === defaultLocale ? `/shop/detail/${entityId.value}` : `/${code}/shop/detail/${entityId.value}`
              await navigateTo(technicalPath)
            }
          } else {
            console.warn('No SEO URLs found at all for this entity')
            return
          }
        }
      } catch (error) {
        console.error('Failed to get SEO URLs:', error)
        return
      }
    } else {
      const currentRoute = useRoute()
      const targetPath = switchLocalePath(code)

      if (targetPath && targetPath !== currentRoute.fullPath) {
        await navigateTo(targetPath)
      } else {
        await updateAllContexts(code)
        return
      }
    }

    if (isShopware.value) {
      await setLocale(code)
    } else {
      await updateAllContexts(code)
    }

    clearPreviewLanguage()

    if (!isShopware.value) {
      setLoading(false)
    }
  } catch (error) {
    console.error('Language switch failed:', error)
    clearPreviewLanguage()
    setLoading(false)
    throw error
  }
}

const computePosition = (
  dropdownList: HTMLUListElement,
  component: Vue,
  { width }: { width: string; top: string; left: string },
) => {
  dropdownList.style.width = width

  const popper = createPopper(component.$refs.toggle as HTMLElement, dropdownList, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, -40],
        },
      },
      {
        name: 'toggleClass',
        enabled: true,
        phase: 'write',
        fn({ state }) {
          component.$el.classList.toggle('drop-up', state.placement === 'top')
        },
      },
    ],
  })

  return () => popper.destroy()
}
</script>

<template>
  <div>
    <ClientOnly>
      <VueSelect
        :key="`lang-${languageCode}`"
        :clearable="false"
        :options="options"
        :model-value="current"
        :searchable="false"
        :append-to-body="true"
        :calculate-position="computePosition"
        @option:selected="onChange"
      >
        <template #selected-option="{ value }">
          <span class="label">
            {{ (value as string).toUpperCase() }}
          </span>
        </template>
        <template #open-indicator="{ toggleAttributes }">
          <span v-bind="toggleAttributes as {}"><Icon name="dropdown" /></span>
        </template>
      </VueSelect>
      <template #fallback>
        <div class="language-fallback">
          {{ languageCode.toUpperCase() }}
          <Icon name="dropdown" />
        </div>
      </template>
    </ClientOnly>
  </div>
</template>
<style lang="scss" scoped>
.v-select {
  --vs-border-radius: 0;
  --vs-border-color: var(--c-blue-40);

  :deep(.vs__dropdown-toggle) {
    padding: 5px 12px 5px 0;
    flex-direction: row-reverse;
  }
  :deep(.vs__selected-options) {
    padding: 0;
  }
  :deep(.vs__selected) {
    padding: 0;
    margin: 0;
    border: 0;
    color: var(--c-primary);
  }
  :deep(.vs__actions) {
    padding: 0;
    svg {
      display: block;
    }
  }
}

.language-fallback {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px 5px 0;
  color: var(--c-primary);
  font-size: inherit;
  cursor: pointer;
}

.vs--single.vs--open :deep(.vs__selected) {
  position: relative;
}
.vs--unsearchable:not(.vs--disabled) :deep(.vs__search) {
  padding: 0;
  position: absolute;
  height: 0;
  width: 0;
  overflow: hidden;
}
</style>
<style lang="scss">
.vs__dropdown-menu {
  padding: 32px 0;
  border: none;
  color: var(--c-primary);
  box-shadow: var(--shadow);
  min-width: 150px;
}
.vs__dropdown-option {
  padding: 9px 12px 11px 32px;
  font-size: 18px;
}
.vs__dropdown-option--highlight {
  color: var(--c-white);
  background-color: var(--c-primary);
}
.vs__dropdown-option--selected {
  display: flex;
  align-items: center;
  color: var(--c-primary);
  background-color: var(--c-background-medium);
}
</style>
